import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { SubscribeManager } from '../class/subscribe-manager.class';
import { safeJsonParse } from '../helpers/dataHelpers';

export type SiteLocation = {
  id: string;
  name: string;
  description: string;
  type?: string;
  group?: string;
  siteId: string;
  zone?: string;
  easting?: number;
  northing?: number;
  elevation?: number;
  latitude?: number;
  longitude?: number;
  domain?: string;
  locationDomain?: string;
  observerType?: string;
};

@Injectable({
  providedIn: 'root',
})
export class LocationsService extends SubscribeManager<SiteLocation[]> {
  private locationsSubject: BehaviorSubject<SiteLocation[] | null>;

  constructor(private selectedLocationService: SelectedLocationService) {
    super();
    const storedLocations = localStorage.getItem('locations');
    const initialLocations: SiteLocation[] | null = safeJsonParse(storedLocations);
    this.locationsSubject = new BehaviorSubject<SiteLocation[] | null>(initialLocations);
  }

  public getLocations(): Observable<SiteLocation[] | null> {
    return this.locationsSubject.asObservable();
  }

  public setLocations(locations: SiteLocation[]): void {
    this.locationsSubject.next(locations);

    // As soon as the locations change, clear the selected location (alias for "All Locations")
    this.selectedLocationService.clearSelectedLocation();

    localStorage.setItem('locations', JSON.stringify(locations));
    this.pushChange(locations);
  }

  public clearLocations(): Observable<null> | null {
    localStorage.removeItem('locations');
    this.selectedLocationService.clearSelectedLocation();
    this.locationsSubject.next(null);
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SelectedLocationService extends SubscribeManager<SiteLocation | null> {
  private selectedLocationSubject: BehaviorSubject<SiteLocation | null>;

  constructor() {
    super();

    const storedSelectedLocation = localStorage.getItem('selectedLocation');
    const initialSelectedLocation: SiteLocation | null = safeJsonParse(storedSelectedLocation);
    this.selectedLocationSubject = new BehaviorSubject<SiteLocation | null>(initialSelectedLocation);
  }

  public getSelectedLocation(): Observable<SiteLocation | null> {
    return this.selectedLocationSubject.asObservable();
  }

  public setSelectedLocation(location: SiteLocation): void {
    this.selectedLocationSubject.next(location);
    localStorage.setItem('selectedLocation', JSON.stringify(location));
    this.pushChange(location);
  }

  public setSelectedLocationById(locationId: string): void {
    const storedLocations = localStorage.getItem('locations');
    const locations: SiteLocation[] | null = safeJsonParse(storedLocations);

    // Start by getting a list of locations to ensure we are setting a valid one
    if (locations && locationId !== 'all') {
      const location = locations.find((loc) => loc.id === locationId);
      if (location) {
        this.setSelectedLocation(location);
      } else {
        this.clearSelectedLocation();
      }
    } else {
      this.clearSelectedLocation();
    }
  }

  public clearSelectedLocation(): Observable<null> | null {
    localStorage.removeItem('selectedLocation');
    this.selectedLocationSubject.next(null);
    this.pushChange(null);
    return null;
  }
}
